import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DraggableProvided, DraggableStateSnapshot, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import './index.less';

import {
  AgentSettingsInput,
  CheckStageSettingsInput,
  GuardrailSettingsState,
  GuardrailsSettingsInput,
  ProviderModel,
} from '../../../../../gamma-api';
import IbButton from '../../../../components/common/IbButton';
import IbConfirmModal from '../../../../components/common/IbConfirmModal';
import IbIcon from '../../../../components/common/IbIcon';
import IbTypography from '../../../../components/common/IbTypography';
import { DEFAULT_AGENT_SETTINGS } from '../../../../components/Settings/AgentGeneral';
import { reorderItems } from '../../../../utils/arrayUtil';
import { getTranslateFunc } from '../../../../utils/stringUtil';
import {
  buildAgentSettingsWithGuardrails,
  createCheckStep,
  GuardrailStageKey,
  ICheckStep,
  normalizeCheckForStage,
  normalizeCheckStageSettings,
  normalizeGuardrailsSettings,
} from '../shared/utils';

import CheckListItem from './CheckListItem';
import CheckModal from './CheckModal';
import GuardrailsFlowChart from './GuardrailsFlowChart';

const MAIN_CLASS_NAME = 'agent-guardrails-tab';
const SECTION_CLASS_NAME = `${MAIN_CLASS_NAME}__section`;
const SUB_STAGE_CLASS_NAME = `${SECTION_CLASS_NAME}__sub-stage`;
const SUB_STAGE_DISABLED_CLASS_NAME = `${SUB_STAGE_CLASS_NAME}_disabled`;
const SUB_STAGE_HEADER_CLASS_NAME = `${SUB_STAGE_CLASS_NAME}__header`;
const EMPTY_CONTENT_CLASS_NAME = `${SUB_STAGE_CLASS_NAME}__empty-content`;
const LIST_CLASS_NAME = `${SUB_STAGE_CLASS_NAME}__list`;

const STAGE_KEYS = [GuardrailStageKey.Input, GuardrailStageKey.Output];

type StageStepsState = Record<GuardrailStageKey, ICheckStep[]>;

interface IDeletingStep {
  stageKey: GuardrailStageKey;
  stepKey: string;
}

export interface IGuardrailsTabProps {
  agentSettings?: AgentSettingsInput;
  providers: ProviderModel[];
  providersLoading: boolean;
  onSave: (settings: AgentSettingsInput) => Promise<void>;
}

const createEmptyStageSteps = (): StageStepsState => ({
  [GuardrailStageKey.Input]: [],
  [GuardrailStageKey.Output]: [],
});

const buildStageSettings = (
  baseStage: CheckStageSettingsInput | undefined,
  steps: ICheckStep[]
): CheckStageSettingsInput => ({
  ...normalizeCheckStageSettings(baseStage),
  checks: steps.map((step, index) => normalizeCheckForStage(step.check, index)),
});

const GuardrailsTab: React.FC<IGuardrailsTabProps> = ({ agentSettings, providers, providersLoading, onSave }) => {
  const { t } = useTranslation();
  const stepKeyCounterRef = useRef(0);

  const [stageSteps, setStageSteps] = useState<StageStepsState>(createEmptyStageSteps);
  const [normalizedGuardrails, setNormalizedGuardrails] = useState<GuardrailsSettingsInput>(() =>
    normalizeGuardrailsSettings(agentSettings?.guardrails)
  );
  const [saving, setSaving] = useState(false);
  const [creationStageKey, setCreationStageKey] = useState<GuardrailStageKey>();
  const [editingStepKey, setEditingStepKey] = useState<string>();
  const [editingStageKey, setEditingStageKey] = useState<GuardrailStageKey>();
  const [deletingStep, setDeletingStep] = useState<IDeletingStep>();

  const getNextStepKey = useCallback(() => {
    stepKeyCounterRef.current += 1;
    return `guardrail-step-${stepKeyCounterRef.current}`;
  }, []);

  useEffect(() => {
    stepKeyCounterRef.current = 0;

    const guardrails = normalizeGuardrailsSettings(agentSettings?.guardrails);
    const nextStageSteps = createEmptyStageSteps();

    const inputChecks = normalizeCheckStageSettings(guardrails.input).checks as ICheckStep['check'][];
    const outputChecks = normalizeCheckStageSettings(guardrails.output).checks as ICheckStep['check'][];

    nextStageSteps[GuardrailStageKey.Input] = inputChecks.map((check) => createCheckStep(getNextStepKey(), check));
    nextStageSteps[GuardrailStageKey.Output] = outputChecks.map((check) => createCheckStep(getNextStepKey(), check));

    setNormalizedGuardrails(guardrails);
    setStageSteps(nextStageSteps);
  }, [agentSettings, getNextStepKey]);

  const editingStep = editingStageKey
    ? stageSteps[editingStageKey].find((step) => step.key === editingStepKey)
    : undefined;

  const buildGuardrails = useCallback(
    (steps: StageStepsState): GuardrailsSettingsInput => ({
      ...normalizedGuardrails,
      masking: normalizedGuardrails.masking,
      input: buildStageSettings(normalizedGuardrails.input, steps[GuardrailStageKey.Input]),
      output: buildStageSettings(normalizedGuardrails.output, steps[GuardrailStageKey.Output]),
    }),
    [normalizedGuardrails]
  );

  const buildSettings = useCallback(
    (steps: StageStepsState): AgentSettingsInput => {
      const settings = agentSettings || DEFAULT_AGENT_SETTINGS;
      return buildAgentSettingsWithGuardrails(settings, buildGuardrails(steps));
    },
    [agentSettings, buildGuardrails]
  );

  const persistStageSteps = useCallback(
    async (steps: StageStepsState) => {
      const nextGuardrails = buildGuardrails(steps);

      setStageSteps(steps);
      setNormalizedGuardrails(nextGuardrails);
      setSaving(true);

      try {
        await onSave(buildSettings(steps));
      } finally {
        setSaving(false);
      }
    },
    [buildGuardrails, buildSettings, onSave]
  );

  const onCreationSave = async (step: ICheckStep) => {
    if (!creationStageKey) {
      return;
    }

    await persistStageSteps({
      ...stageSteps,
      [creationStageKey]: [...stageSteps[creationStageKey], { ...step, key: getNextStepKey() }],
    });
    setCreationStageKey(undefined);
  };

  const onEditingSave = async (step: ICheckStep) => {
    if (!editingStageKey) {
      return;
    }

    await persistStageSteps({
      ...stageSteps,
      [editingStageKey]: stageSteps[editingStageKey].map((item) =>
        item.key === editingStepKey ? { ...step, key: item.key } : item
      ),
    });
    setEditingStepKey(undefined);
    setEditingStageKey(undefined);
  };

  const onStepEnabledChange = async (stageKey: GuardrailStageKey, stepKey: string, enabled: boolean) => {
    await persistStageSteps({
      ...stageSteps,
      [stageKey]: stageSteps[stageKey].map((step) =>
        step.key === stepKey
          ? {
              ...step,
              check: {
                ...step.check,
                state: enabled ? GuardrailSettingsState.Enabled : GuardrailSettingsState.Disabled,
              },
            }
          : step
      ),
    });
  };

  const onStepDeleteRequest = (stageKey: GuardrailStageKey, stepKey: string) => {
    setDeletingStep({ stageKey, stepKey });
  };

  const onCreationClose = () => {
    setCreationStageKey(undefined);
  };

  const onEditingClose = () => {
    setEditingStepKey(undefined);
    setEditingStageKey(undefined);
  };

  const onDeleteCancel = () => {
    setDeletingStep(undefined);
  };

  const onStepDeleteConfirm = async () => {
    if (!deletingStep) {
      return;
    }

    await persistStageSteps({
      ...stageSteps,
      [deletingStep.stageKey]: stageSteps[deletingStep.stageKey].filter((step) => step.key !== deletingStep.stepKey),
    });

    if (editingStepKey === deletingStep.stepKey) {
      setEditingStepKey(undefined);
      setEditingStageKey(undefined);
    }

    setDeletingStep(undefined);
  };

  const moveStep = async (stageKey: GuardrailStageKey, stepKey: string, direction: -1 | 1) => {
    const stepIndex = stageSteps[stageKey].findIndex((step) => step.key === stepKey);
    const nextIndex = stepIndex + direction;

    if (stepIndex < 0 || nextIndex < 0 || nextIndex >= stageSteps[stageKey].length) {
      return;
    }

    const nextSteps = reorderItems(stageSteps[stageKey], stepIndex, nextIndex);

    if (nextSteps === stageSteps[stageKey]) {
      return;
    }

    await persistStageSteps({
      ...stageSteps,
      [stageKey]: nextSteps,
    });
  };

  const onDragEnd = useCallback(
    async (result: DropResult) => {
      const { destination, source } = result;

      if (!destination) {
        return;
      }

      const sourceStageKey = source.droppableId as GuardrailStageKey;
      const destinationStageKey = destination.droppableId as GuardrailStageKey;

      if (!STAGE_KEYS.includes(sourceStageKey) || !STAGE_KEYS.includes(destinationStageKey)) {
        return;
      }

      if (
        sourceStageKey === destinationStageKey &&
        (source.index === destination.index || destination.index >= stageSteps[sourceStageKey].length)
      ) {
        return;
      }

      const nextStageSteps = {
        ...stageSteps,
        [sourceStageKey]: [...stageSteps[sourceStageKey]],
        [destinationStageKey]: [...stageSteps[destinationStageKey]],
      };
      const [sourceStep] = nextStageSteps[sourceStageKey].splice(source.index, 1);

      if (!sourceStep) {
        return;
      }

      const movedStep =
        sourceStageKey === destinationStageKey
          ? sourceStep
          : {
              ...sourceStep,
              check: normalizeCheckForStage(sourceStep.check, sourceStep.check.order ?? 0),
            };

      nextStageSteps[destinationStageKey].splice(destination.index, 0, movedStep);

      await persistStageSteps(nextStageSteps);
    },
    [persistStageSteps, stageSteps]
  );

  const stageTranslate = getTranslateFunc('Agent: Guardrails: Stage:');

  const getStageTitle = (stageKey: GuardrailStageKey): string =>
    stageKey === GuardrailStageKey.Output ? stageTranslate('Output: Title') : stageTranslate('Input: Title');

  const getStageDescription = (stageKey: GuardrailStageKey): string =>
    stageKey === GuardrailStageKey.Output
      ? stageTranslate('Output: Description')
      : stageTranslate('Input: Description');

  const renderCheckStage = (stageKey: GuardrailStageKey, disabled = false) => {
    const steps = stageSteps[stageKey];
    const className = [SUB_STAGE_CLASS_NAME, disabled ? SUB_STAGE_DISABLED_CLASS_NAME : ''].filter(Boolean).join(' ');

    const onAddButtonClick = () => {
      setCreationStageKey(stageKey);
    };

    const onStepDelete = (stepKey: string) => {
      onStepDeleteRequest(stageKey, stepKey);
    };

    const onStepChange = (stepKey: string, enabled: boolean) => {
      onStepEnabledChange(stageKey, stepKey, enabled);
    };

    const onStepMove = (stepKey: string, direction: -1 | 1) => {
      moveStep(stageKey, stepKey, direction);
    };

    const onStepSetup = (stepKey: string) => {
      setEditingStageKey(stageKey);
      setEditingStepKey(stepKey);
    };

    const renderEmptyContent = () => (
      <div className={EMPTY_CONTENT_CLASS_NAME}>
        <IbTypography.Paragraph disabled type="secondary">
          {t('Agent: Guardrails: Steps: Empty')}
        </IbTypography.Paragraph>
      </div>
    );

    const renderCheckListItem = (
      step: ICheckStep,
      index: number,
      draggableProvided: DraggableProvided,
      draggableSnapshot: DraggableStateSnapshot
    ) => (
      <CheckListItem
        draggableProps={draggableProvided.draggableProps}
        dragHandleProps={draggableProvided.dragHandleProps}
        index={index}
        innerRef={draggableProvided.innerRef}
        isDragging={draggableSnapshot.isDragging}
        saving={saving || disabled}
        step={step}
        total={steps.length}
        onDelete={onStepDelete}
        onMove={onStepMove}
        onSetup={onStepSetup}
        onStepChange={onStepChange}
      />
    );

    const renderDraggableStep = (step: ICheckStep, index: number) => {
      const renderDraggableContent = (
        draggableProvided: DraggableProvided,
        draggableSnapshot: DraggableStateSnapshot
      ) => renderCheckListItem(step, index, draggableProvided, draggableSnapshot);

      return (
        <Draggable key={step.key} draggableId={step.key} index={index} isDragDisabled={saving || disabled}>
          {renderDraggableContent}
        </Draggable>
      );
    };

    const renderDroppableContent = (droppableProvided: DroppableProvided) => (
      <div ref={droppableProvided.innerRef} className={LIST_CLASS_NAME} {...droppableProvided.droppableProps}>
        {!steps.length ? renderEmptyContent() : steps.map(renderDraggableStep)}
        {droppableProvided.placeholder}
      </div>
    );

    return (
      <div className={className}>
        <div className={SUB_STAGE_HEADER_CLASS_NAME}>
          <div>
            <IbTypography>
              <h5>{getStageTitle(stageKey)}</h5>
            </IbTypography>
            <IbTypography.Paragraph type="secondary">{getStageDescription(stageKey)}</IbTypography.Paragraph>
          </div>
          <IbButton
            disabled={saving || disabled}
            icon={<IbIcon iconName="add-one" />}
            type="link"
            onClick={onAddButtonClick}
          >
            {t('Agent: Guardrails: Steps: Add')}
          </IbButton>
        </div>
        <Droppable droppableId={stageKey} isDropDisabled={saving || disabled}>
          {renderDroppableContent}
        </Droppable>
      </div>
    );
  };

  const onFlowChartAddStep = useCallback((stageKey: GuardrailStageKey) => {
    setCreationStageKey(stageKey);
  }, []);

  return (
    <div className={MAIN_CLASS_NAME}>
      <GuardrailsFlowChart
        masking={normalizedGuardrails.masking}
        saving={saving}
        stageSteps={stageSteps}
        onAddStep={onFlowChartAddStep}
      />
      <DragDropContext onDragEnd={onDragEnd}>
        <div className={SECTION_CLASS_NAME}>{renderCheckStage(GuardrailStageKey.Input)}</div>
        <div className={SECTION_CLASS_NAME}>{renderCheckStage(GuardrailStageKey.Output)}</div>
      </DragDropContext>
      <CheckModal
        isCreation
        providers={providers}
        providersLoading={providersLoading}
        stageKey={creationStageKey}
        visible={!!creationStageKey}
        onClose={onCreationClose}
        onSave={onCreationSave}
      />
      <CheckModal
        providers={providers}
        providersLoading={providersLoading}
        stageKey={editingStageKey}
        step={editingStep}
        visible={!!editingStep}
        onClose={onEditingClose}
        onSave={onEditingSave}
      />
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onStepDeleteConfirm}>
              {t('Agent: Guardrails: Steps: Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onDeleteCancel}>
              {t('Cancel')}
            </IbButton>
          </>
        }
        loading={saving}
        title={t('Agent: Guardrails: Steps: DeleteConfirm: Title')}
        visible={!!deletingStep}
        onCancel={onDeleteCancel}
      >
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Steps: DeleteConfirm: Description')}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    </div>
  );
};

export default GuardrailsTab;
